/**
 * BSA/AML risk assessments — list and create.
 *
 * The way into the assessment screen. Creating one generates the full factor set
 * from the published FFIEC baseline, every factor inactive until confirmed, so
 * the new assessment opens as a question rather than an assertion.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DocumentTextIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { bsaApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';

type RiskTier = 'LOW' | 'MODERATE_LOW' | 'MODERATE' | 'MODERATE_HIGH' | 'HIGH';

const TIER_LABEL: Record<RiskTier, string> = {
  LOW: 'Low', MODERATE_LOW: 'Moderate-low', MODERATE: 'Moderate',
  MODERATE_HIGH: 'Moderate-high', HIGH: 'High',
};
const TIER_CLS: Record<RiskTier, string> = {
  LOW: 'bg-emerald-100 text-emerald-800',
  MODERATE_LOW: 'bg-lime-100 text-lime-800',
  MODERATE: 'bg-amber-100 text-amber-800',
  MODERATE_HIGH: 'bg-orange-100 text-orange-800',
  HIGH: 'bg-red-100 text-red-800',
};

interface AssessmentRow {
  id: string;
  title: string;
  scope: string;
  scopeLabel: string | null;
  status: string;
  residualRiskTier: RiskTier | null;
  assessmentPeriodStart: string;
  assessmentPeriodEnd: string;
  finalizedAt: string | null;
  createdAt: string;
  _count: { factors: number };
}

export default function BsaAssessmentListPage() {
  const { teamId } = useTeamId();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [title, setTitle] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['bsa-assessments', teamId],
    queryFn: async () => (await bsaApi.assessments.list(teamId!)).data,
    enabled: !!teamId,
  });

  const create = useMutation({
    mutationFn: async () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), 0, 1);
      const end = new Date(now.getFullYear(), 11, 31);
      return (
        await bsaApi.assessments.create(teamId!, {
          title: title.trim() || `BSA/AML Risk Assessment ${now.getFullYear()}`,
          scope: 'INSTITUTION',
          assessmentPeriodStart: start.toISOString(),
          assessmentPeriodEnd: end.toISOString(),
        })
      ).data;
    },
    onSuccess: (a: { id: string }) => {
      qc.invalidateQueries({ queryKey: ['bsa-assessments', teamId] });
      navigate(`/bsa/assessments/${a.id}`);
    },
  });

  const assessments: AssessmentRow[] = data?.assessments ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">BSA/AML risk assessments</h1>
          <p className="mt-1 text-sm text-gray-600">
            Scored across the four FFIEC pillars, with every rating traceable to published guidance.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={`BSA/AML Risk Assessment ${new Date().getFullYear()}`}
            className="rounded-md border-gray-300 text-sm focus:border-gray-500 focus:ring-gray-500"
          />
          <button
            type="button"
            onClick={() => create.mutate()}
            disabled={create.isPending}
            className="rounded-md bg-gray-900 px-3 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
          >
            {create.isPending ? 'Creating…' : 'New assessment'}
          </button>
        </div>
      </div>

      {/* Reference data is a hard prerequisite — generation refuses without it,
          because factors with no reference tier would silently disable
          deviation-requires-justification. Say so plainly rather than showing a
          raw 409. */}
      {create.isError && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-gray-800">
          <p className="font-medium">Couldn’t create the assessment.</p>
          <p className="mt-1 text-xs">
            {(create.error as { response?: { data?: { error?: string } } })?.response?.data?.error ??
              'If BSA reference data has not been loaded for this environment, generation is blocked on purpose — an assessment without a published baseline can’t support a risk rating.'}
          </p>
        </div>
      )}

      {isLoading && <div className="py-16 text-center text-sm text-gray-500">Loading…</div>}

      {!isLoading && assessments.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white px-6 py-12 text-center">
          <DocumentTextIcon className="mx-auto h-8 w-8 text-gray-400" aria-hidden />
          <h3 className="mt-3 text-sm font-medium text-gray-900">No assessments yet</h3>
          <p className="mx-auto mt-1 max-w-md text-sm text-gray-500">
            A new assessment starts with the full FFIEC factor set, every factor unconfirmed. You
            mark what the institution actually does, rate the controls, and the residual tiers follow.
          </p>
        </div>
      )}

      {assessments.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="border-b border-gray-200 bg-gray-50">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600">Assessment</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600">Period</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600">Factors</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600">Residual</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {assessments.map((a) => (
                <tr key={a.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <Link to={`/bsa/assessments/${a.id}`} className="font-medium text-gray-900 hover:underline">
                      {a.title}
                    </Link>
                    {a.scopeLabel && <div className="text-xs text-gray-500">{a.scopeLabel}</div>}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {new Date(a.assessmentPeriodStart).toLocaleDateString()} –{' '}
                    {new Date(a.assessmentPeriodEnd).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-gray-600">{a._count.factors}</td>
                  <td className="px-4 py-3">
                    {a.residualRiskTier ? (
                      <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${TIER_CLS[a.residualRiskTier]}`}>
                        {TIER_LABEL[a.residualRiskTier]}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">Not yet scored</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {a.status === 'FINALIZED' ? (
                      <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                        <LockClosedIcon className="h-3 w-3" aria-hidden /> Finalized
                      </span>
                    ) : (
                      <span className="text-xs text-gray-500">Draft</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
